import { useState, useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import {
  Download,
  Loader2,
  TrendingUp,
  ArrowLeft,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
} from 'lucide-react'
import { ReportFilters, getDateRange as getReportDateRange } from '@/components/ReportFilters'
import type { ReportFilterValues } from '@/components/ReportFilters'
import { CHART_COLORS } from '@/lib/theme'
import { formatCurrency, formatDate } from '@/lib/utils'
import { exportToCSV } from '@/utils/csvExport'
import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/lib/api'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { format } from 'date-fns'
import { ChartTooltipContent } from '@/components/ui/chart-tooltip'

const COLORS = CHART_COLORS

function formatMonth(dateStr: string) {
  const d = new Date(dateStr)
  return format(d, 'MMM yyyy')
}

export default function ClosedWonMonthly() {
  const navigate = useNavigate()
  const [filters, setFilters] = useState<ReportFilterValues>(() =>
    getReportDateRange('fiscal_year')
  )
  const [sortField, setSortField] = useState('close_date')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortField(field)
      setSortOrder('asc')
    }
  }

  const { data, isLoading, error } = useQuery({
    queryKey: ['reports', 'closed-won-monthly', filters],
    queryFn: async () => {
      const params = new URLSearchParams()
      if (filters.startDate) params.set('startDate', filters.startDate)
      if (filters.endDate) params.set('endDate', filters.endDate)
      if (filters.owner_id) params.set('owner_id', filters.owner_id)
      const response = await (apiClient as any).client.get(
        `/reports/closed-won-monthly?${params.toString()}`
      )
      return response.data?.data
    },
  })

  // Build stacked chart data: { month: 'Jan 2026', 'Owner A': amount, 'Owner B': amount }
  const { chartData, owners } = useMemo(() => {
    if (!data?.summary) return { chartData: [], owners: [] }
    const monthMap: Record<string, Record<string, number>> = {}
    const allOwners = new Set<string>()

    for (const row of data.summary) {
      const monthLabel = formatMonth(row.month)
      if (!monthMap[monthLabel]) monthMap[monthLabel] = { month: monthLabel } as any
      const ownerName = row.owner_name || 'Unknown'
      const entry = monthMap[monthLabel]
      if (entry) entry[ownerName] = parseFloat(row.total_amount) || 0
      allOwners.add(ownerName)
    }

    return {
      chartData: Object.values(monthMap),
      owners: Array.from(allOwners).sort(),
    }
  }, [data])

  // Group details by month
  const groupedDetails = useMemo(() => {
    if (!data?.details) return []
    const sorted = [...data.details].sort((a: any, b: any) => {
      const aVal = (a[sortField] ?? '').toString().toLowerCase()
      const bVal = (b[sortField] ?? '').toString().toLowerCase()
      if (['amount_cad', 'weighted_amount'].includes(sortField)) {
        const diff = (parseFloat(aVal) || 0) - (parseFloat(bVal) || 0)
        return sortOrder === 'asc' ? diff : -diff
      }
      const cmp = aVal.localeCompare(bVal)
      return sortOrder === 'asc' ? cmp : -cmp
    })

    const groups: { month: string; rows: any[]; total_amount: number; weighted_amount: number }[] =
      []
    let currentGroup: (typeof groups)[0] | null = null

    for (const row of sorted) {
      const monthLabel = row.close_date ? formatMonth(row.close_date) : 'Unknown'
      if (!currentGroup || currentGroup.month !== monthLabel) {
        currentGroup = { month: monthLabel, rows: [], total_amount: 0, weighted_amount: 0 }
        groups.push(currentGroup)
      }
      currentGroup.rows.push(row)
      currentGroup.total_amount += parseFloat(row.amount_cad) || 0
      currentGroup.weighted_amount += parseFloat(row.weighted_amount) || 0
    }
    return groups
  }, [data?.details, sortField, sortOrder])

  const handleExport = () => {
    if (data?.details) {
      exportToCSV({
        filename: 'Closed_Won_Monthly',
        data: data.details,
        columns: [
          'close_date',
          'owner_name',
          'account_name',
          'name',
          'stage_name',
          'created_date',
          'lead_source',
          'amount_cad',
          'currency_iso_code',
          'weighted_amount',
        ],
        headers: [
          'Close Date',
          'Owner',
          'Account',
          'Opportunity',
          'Stage',
          'Created Date',
          'Lead Source',
          'Amount (CAD)',
          'Source Currency',
          'Weighted Amount (CAD)',
        ],
      })
    }
  }

  const columns = [
    { field: 'close_date', label: 'Close Date' },
    { field: 'owner_name', label: 'Owner' },
    { field: 'account_name', label: 'Account Name' },
    { field: 'name', label: 'Opportunity Name' },
    { field: 'stage_name', label: 'Stage' },
    { field: 'created_date', label: 'Created Date' },
    { field: 'lead_source', label: 'Lead Source' },
    { field: 'amount_cad', label: 'Amount (CAD)' },
    { field: 'currency_iso_code', label: 'Source Currency' },
    { field: 'weighted_amount', label: 'Weighted Amount (CAD)' },
  ]

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/reports')}
            aria-label="Back to reports"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-[#2E7D5B]" aria-hidden="true" />
              <h1 className="text-2xl font-bold">Month over Month - Closed Won</h1>
              <Badge variant="secondary">Pre-built</Badge>
            </div>
            <p className="text-muted-foreground mt-1">
              Closed won opportunities by month
              {data?.totalRecords ? ` — ${data.totalRecords.toLocaleString()} records` : ''}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <ReportFilters
            value={filters}
            onChange={setFilters}
            defaultPeriod="fiscal_year"
            datePresets={[
              'this_month',
              'this_quarter',
              'fiscal_year',
              'calendar_year',
              'last_12_months',
              'all',
              'custom',
            ]}
          />
          <Button
            variant="outline"
            onClick={handleExport}
            disabled={!data?.details?.length}
            aria-label="Export to CSV"
          >
            <Download className="mr-2 h-4 w-4" aria-hidden="true" />
            CSV
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {data && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Total Records</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{(data.totalRecords ?? 0).toLocaleString()}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Total Amount (CAD)</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{formatCurrency(data.grandTotalAmount ?? 0, 'CAD')}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Total Weighted Amount (CAD)</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{formatCurrency(data.grandWeightedAmount ?? 0, 'CAD')}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" aria-hidden="true" />
          <span className="ml-2">Loading report...</span>
        </div>
      )}

      {error && (
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <p className="text-destructive">Error loading report. Please try again.</p>
          </CardContent>
        </Card>
      )}

      {!isLoading && data && (
        <>
          {/* Stacked Bar Chart by month + owner */}
          <Card>
            <CardHeader>
              <CardTitle>Closed Won by Month</CardTitle>
              <CardDescription>Amount by opportunity owner per month</CardDescription>
            </CardHeader>
            <CardContent>
              {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                    <Tooltip
                      content={<ChartTooltipContent />}
                      formatter={(value: number) => formatCurrency(value, 'CAD')}
                    />
                    <Legend />
                    {owners.map((owner, i) => (
                      <Bar
                        key={owner}
                        dataKey={owner}
                        stackId="a"
                        fill={COLORS[i % COLORS.length]}
                        name={owner}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-center py-8 text-muted-foreground">
                  No data for selected period
                </p>
              )}
            </CardContent>
          </Card>

          {/* Detail Table grouped by month */}
          <Card>
            <CardHeader>
              <CardTitle>Opportunity Details</CardTitle>
              <CardDescription>
                {data.details?.length || 0} records (complete result)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      {columns.map((col) => (
                        <TableHead key={col.field}>
                          <button
                            className="flex items-center gap-1 hover:text-foreground focus:outline-none focus:ring-2 focus:ring-primary rounded"
                            onClick={() => handleSort(col.field)}
                            aria-label={`Sort by ${col.label}`}
                          >
                            {col.label}
                            {sortField === col.field ? (
                              sortOrder === 'asc' ? (
                                <ArrowUp className="h-3 w-3" aria-hidden="true" />
                              ) : (
                                <ArrowDown className="h-3 w-3" aria-hidden="true" />
                              )
                            ) : (
                              <ArrowUpDown className="h-3 w-3 opacity-30" aria-hidden="true" />
                            )}
                          </button>
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {groupedDetails.map((group) => (
                      <>
                        {/* Month group header */}
                        <TableRow key={`header-${group.month}`} className="bg-muted/50">
                          <TableCell colSpan={10} className="font-semibold py-2">
                            {group.month} ({group.rows.length})
                          </TableCell>
                        </TableRow>
                        {/* Opportunity rows */}
                        {group.rows.map((row: any) => (
                          <TableRow key={row.id}>
                            <TableCell>{formatDate(row.close_date)}</TableCell>
                            <TableCell>{row.owner_name || '-'}</TableCell>
                            <TableCell>
                              {row.account_id ? (
                                <Link
                                  to={`/accounts/${row.account_id}`}
                                  className="text-primary hover:underline"
                                >
                                  {row.account_name || '-'}
                                </Link>
                              ) : (
                                row.account_name || '-'
                              )}
                            </TableCell>
                            <TableCell>
                              <Link
                                to={`/opportunities/${row.id}`}
                                className="text-primary hover:underline"
                              >
                                {row.name}
                              </Link>
                            </TableCell>
                            <TableCell>
                              <Badge variant="secondary">{row.stage_name || '-'}</Badge>
                            </TableCell>
                            <TableCell>{formatDate(row.created_date)}</TableCell>
                            <TableCell>{row.lead_source || '-'}</TableCell>
                            <TableCell className="text-right font-mono">
                              {row.amount_cad != null
                                ? formatCurrency(parseFloat(row.amount_cad), 'CAD')
                                : '-'}
                            </TableCell>
                            <TableCell>{row.currency_iso_code || '-'}</TableCell>
                            <TableCell className="text-right font-mono">
                              {row.weighted_amount != null
                                ? formatCurrency(parseFloat(row.weighted_amount), 'CAD')
                                : '-'}
                            </TableCell>
                          </TableRow>
                        ))}
                        {/* Subtotal row */}
                        <TableRow
                          key={`subtotal-${group.month}`}
                          className="border-t-2 font-semibold"
                        >
                          <TableCell colSpan={7} className="text-right">
                            Subtotal
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            {formatCurrency(group.total_amount, 'CAD')}
                          </TableCell>
                          <TableCell />
                          <TableCell className="text-right font-mono">
                            {formatCurrency(group.weighted_amount, 'CAD')}
                          </TableCell>
                        </TableRow>
                      </>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  )
}
